import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/apiUser";

/**
 * useAuth Hook
 * Manages user authentication state and methods.
 */
export function useAuth() {
    const navigate = useNavigate();
    const [user, setUser] = useState(() => {
        const saved = localStorage.getItem("user");
        return saved ? JSON.parse(saved) : null;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Sync state with localStorage if it changes in another tab
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === "user") {
                setUser(e.newValue ? JSON.parse(e.newValue) : null);
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    const clearError = useCallback(() => setError(null), []);

    const login = async (email, password) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.post("/auth/login", { email, password });
            if (res.data.status === "success") {
                const { access, user: userData } = res.data.data;
                localStorage.setItem("access", access);
                localStorage.setItem("user", JSON.stringify(userData));
                setUser(userData);
                return { status: "success", user: userData };
            }
            const msg = res.data.message || "Login failed";
            setError(msg);
            return { status: "failed", message: msg };
        } catch (err) {
            const msg = err.response?.data?.message || "Login failed";
            setError(msg);
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    };

    const forgotPassword = async (email) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.post("/auth/forgot-password", { email });
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to send reset code";
            setError(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async (email, otp) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.post("/auth/verify-otp", { email, otp });
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Invalid or expired OTP";
            setError(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async (email, token, newPassword) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.post("/auth/reset-password", {
                email,
                token,
                new_password: newPassword
            });
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to reset password";
            setError(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await API.post("/auth/logout");
        } catch (err) {
            console.error("Backend logout failed:", err);
        }

        // Navigate first to avoid showing guest/cleared state on current page
        navigate("/login", { replace: true });

        // Then clear storage and state
        localStorage.removeItem("access");
        localStorage.removeItem("user");
        setUser(null);
    };

    return {
        user,
        loading,
        error,
        clearError,
        login,
        logout,
        forgotPassword,
        verifyOtp,
        resetPassword,
        isAuthenticated: !!user,
    };
}
